import type { QueryClient } from '@tanstack/react-query'
import type { Match } from './types/match'
import type { MatchOverviewResponse } from './types/api'

export interface CacheUpdateParams {
  rowsToAdd?: Match[]
  rowsToRemove?: string[] // Array of match IDs to remove
  rowsToUpdate?: Partial<Match>[] // Array of partial match objects for updates
}

export interface LinkActionResponse {
  rows_to_add?: Match[]
  rows_to_remove?: string[]
}

export type ApiResponseData = MatchOverviewResponse | Match | Match[] | LinkActionResponse

export interface MatchQueryData {
  rows: Match[]
  [key: string]: unknown // Allow other fields from API response
}

export function createMatchCacheUtils(queryClient: QueryClient) {
  function updateMatchOverviewCache(params: CacheUpdateParams) {
    const { rowsToAdd = [], rowsToRemove = [], rowsToUpdate = [] } = params

    const queryKeyPatterns = [
      ['supplier_rec_match_overview_open'],
      ['supplier_rec_match_overview_approved'],
      ['supplier_rec_match_overview_voided'],
    ]

    queryKeyPatterns.forEach((pattern) => {
      queryClient.setQueriesData({ queryKey: pattern }, (oldData: MatchQueryData | undefined) => {
        if (!oldData || !oldData.rows) return oldData

        let updatedRows = [...oldData.rows]

        if (rowsToRemove.length > 0) {
          updatedRows = updatedRows.filter((row) => !rowsToRemove.includes(row.id))
        }

        if (rowsToUpdate.length > 0) {
          updatedRows = updatedRows.map((row) => {
            const updatedMatch = rowsToUpdate.find((updated) => updated.id === row.id)
            return updatedMatch ? { ...row, ...updatedMatch } : row
          })
        }

        if (rowsToAdd.length > 0) {
          for (const matchToAdd of rowsToAdd) {
            const existingIndex = updatedRows.findIndex((row) => row.id === matchToAdd.id)
            if (existingIndex >= 0) {
              updatedRows[existingIndex] = { ...updatedRows[existingIndex], ...matchToAdd }
            } else {
              updatedRows.unshift(matchToAdd)
            }
          }
        }

        return {
          ...oldData,
          rows: updatedRows,
        }
      })
    })

    queryClient.setQueriesData(
      { queryKey: ['post', '/supplier_rec/match/overview'] },
      (oldData: MatchQueryData | undefined) => {
        if (!oldData || !oldData.rows) return oldData

        let updatedRows = [...oldData.rows]

        // Remove rows first
        if (rowsToRemove.length > 0) {
          updatedRows = updatedRows.filter((row) => !rowsToRemove.includes(row.id))
        }

        // Update existing rows
        if (rowsToUpdate.length > 0) {
          updatedRows = updatedRows.map((row) => {
            const updatedMatch = rowsToUpdate.find((updated) => updated.id === row.id)
            return updatedMatch ? { ...row, ...updatedMatch } : row
          })
        }

        // Add new matches or update existing ones
        if (rowsToAdd.length > 0) {
          for (const matchToAdd of rowsToAdd) {
            const existingIndex = updatedRows.findIndex((row) => row.id === matchToAdd.id)
            if (existingIndex >= 0) {
              // Update existing match (bulk merge case)
              updatedRows[existingIndex] = { ...updatedRows[existingIndex], ...matchToAdd }
            } else {
              // Add new match (invoice creation case)
              updatedRows.unshift(matchToAdd)
            }
          }
        }

        return {
          ...oldData,
          rows: updatedRows,
        }
      }
    )

    queryClient.setQueriesData({ queryKey: ['matchOverview'] }, (oldData: MatchQueryData | undefined) => {
      if (!oldData || !oldData.rows) return oldData

      let updatedRows = [...oldData.rows]

      // Remove rows first
      if (rowsToRemove.length > 0) {
        updatedRows = updatedRows.filter((row) => !rowsToRemove.includes(row.id))
      }

      // Update existing rows
      if (rowsToUpdate.length > 0) {
        updatedRows = updatedRows.map((row) => {
          const updatedMatch = rowsToUpdate.find((updated) => updated.id === row.id)
          return updatedMatch ? { ...row, ...updatedMatch } : row
        })
      }

      // Add new matches or update existing ones
      if (rowsToAdd.length > 0) {
        for (const matchToAdd of rowsToAdd) {
          const existingIndex = updatedRows.findIndex((row) => row.id === matchToAdd.id)
          if (existingIndex >= 0) {
            // Update existing match (bulk merge case)
            updatedRows[existingIndex] = { ...updatedRows[existingIndex], ...matchToAdd }
          } else {
            // Add new match (invoice creation case)
            updatedRows.unshift(matchToAdd)
          }
        }
      }

      return {
        ...oldData,
        rows: updatedRows,
      }
    })
  }

  function processApiResponse(
    response: ApiResponseData,
    actionType: 'approve' | 'hold' | 'unhold' | 'void' | 'unvoid' | 'link'
  ): CacheUpdateParams {
    if (!response || response === null || response === undefined) {
      console.warn('API response is null/undefined for cache update:', { response, actionType })
      return {}
    }

    if ('rows_to_add' in response || 'rows_to_remove' in response) {
      const linkResponse = response as LinkActionResponse
      return {
        rowsToAdd: linkResponse.rows_to_add || [],
        rowsToRemove: linkResponse.rows_to_remove || [],
      }
    }

    if ('id' in response && !Array.isArray(response)) {
      const matchResponse = response as Match
      return {
        rowsToUpdate: [matchResponse],
      }
    }

    if (Array.isArray(response)) {
      return {
        rowsToUpdate: response,
      }
    }

    console.warn('Unrecognized API response format for cache update:', response)
    return {}
  }

  function updateMatchStatusTransition(
    matchIds: string[],
    oldStatus: string,
    newStatus: string,
    updatedMatchData: any[] = []
  ) {
    const getQueryType = (status: string) => {
      if (status === 'approved') return 'approved'
      if (status === 'void') return 'voided'
      return 'open'
    }

    const sourceQueryType = getQueryType(oldStatus)
    const targetQueryType = getQueryType(newStatus)

    if (sourceQueryType !== targetQueryType) {
      queryClient.setQueriesData(
        { queryKey: [`supplier_rec_match_overview_${sourceQueryType}`] },
        (oldData: MatchQueryData | undefined) => {
          if (!oldData || !oldData.rows) return oldData

          return {
            ...oldData,
            rows: oldData.rows.filter((row) => !matchIds.includes(row.id)),
          }
        }
      )

      if (updatedMatchData.length > 0) {
        queryClient.setQueriesData(
          { queryKey: [`supplier_rec_match_overview_${targetQueryType}`] },
          (oldData: MatchQueryData | undefined) => {
            if (!oldData || !oldData.rows) return oldData

            const newRows = [...oldData.rows]

            updatedMatchData.forEach((matchToAdd) => {
              const existingIndex = newRows.findIndex((row) => row.id === matchToAdd.id)
              if (existingIndex >= 0) {
                newRows[existingIndex] = { ...newRows[existingIndex], ...matchToAdd }
              } else {
                newRows.unshift(matchToAdd)
              }
            })

            return {
              ...oldData,
              rows: newRows,
            }
          }
        )
      }
    } else {
      queryClient.setQueriesData(
        { queryKey: [`supplier_rec_match_overview_${sourceQueryType}`] },
        (oldData: MatchQueryData | undefined) => {
          if (!oldData || !oldData.rows) return oldData

          let updatedRows = [...oldData.rows]

          if (updatedMatchData.length > 0) {
            updatedRows = updatedRows.map((row) => {
              const updatedMatch = updatedMatchData.find((updated) => updated.id === row.id)
              return updatedMatch ? { ...row, ...updatedMatch } : row
            })
          }

          return {
            ...oldData,
            rows: updatedRows,
          }
        }
      )
    }
  }

  function createOptimisticApprove(matchIds: string[], reasonCode?: string): CacheUpdateParams {
    const now = new Date().toISOString()

    return {
      rowsToUpdate: matchIds.map((id) => ({
        id,
        status: 'approved' as const,
        updated_at: now,
        approval_reason: reasonCode,
      })),
    }
  }

  function createOptimisticHold(matchIds: string[], reason: string, user: string): CacheUpdateParams {
    const now = new Date().toISOString()

    return {
      rowsToUpdate: matchIds.map((id) => ({
        id,
        status: 'hold' as const,
        updated_at: now,
        hold_reason: reason,
        hold_date: now,
        hold_by: user,
      })),
    }
  }

  function createOptimisticUnhold(matchIds: string[]): CacheUpdateParams {
    const now = new Date().toISOString()

    return {
      rowsToUpdate: matchIds.map((id) => ({
        id,
        status: 'unapproved' as const,
        updated_at: now,
        hold_reason: undefined,
        hold_date: undefined,
      })),
    }
  }

  function createOptimisticVoid(matchId: string): CacheUpdateParams {
    const now = new Date().toISOString()

    return {
      rowsToUpdate: [
        {
          id: matchId,
          status: 'void' as const,
          updated_at: now,
        },
      ],
    }
  }

  function createOptimisticUnvoid(matchId: string): CacheUpdateParams {
    const now = new Date().toISOString()

    return {
      rowsToUpdate: [
        {
          id: matchId,
          status: 'unmatched' as const,
          updated_at: now,
        },
      ],
    }
  }

  async function cancelMatchQueries() {
    await Promise.all([
      queryClient.cancelQueries({
        predicate: (query) => {
          const key = query.queryKey[0]
          return (
            key === 'supplier_rec_match_overview_open' ||
            key === 'supplier_rec_match_overview_approved' ||
            key === 'supplier_rec_match_overview_voided'
          )
        },
      }),
      queryClient.cancelQueries({
        queryKey: ['post', '/supplier_rec/match/overview'],
      }),
      queryClient.cancelQueries({
        queryKey: ['matchOverview'],
      }),
    ])
  }

  function getPreviousQueryData() {
    const openQueries = queryClient.getQueriesData({
      queryKey: ['supplier_rec_match_overview_open'],
    })
    const approvedQueries = queryClient.getQueriesData({
      queryKey: ['supplier_rec_match_overview_approved'],
    })
    const voidedQueries = queryClient.getQueriesData({
      queryKey: ['supplier_rec_match_overview_voided'],
    })

    return [...openQueries, ...approvedQueries, ...voidedQueries]
  }

  function rollbackQueryData(previousData: [unknown, unknown][] | undefined) {
    if (previousData && previousData.length > 0) {
      previousData.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey as string[], data)
      })
    }
  }

  return {
    updateMatchOverviewCache,
    updateMatchStatusTransition,
    processApiResponse,
    createOptimisticApprove,
    createOptimisticHold,
    createOptimisticUnhold,
    createOptimisticVoid,
    createOptimisticUnvoid,
    cancelMatchQueries,
    getPreviousQueryData,
    rollbackQueryData,
  }
}

export function useMatchCacheUtils(queryClient: QueryClient) {
  return createMatchCacheUtils(queryClient)
}
